import { Address } from "viem"
import { GetVaultData } from "../../query"
import { FormattedAssetValue, FormattedValue } from "../types"

export interface Asset {
  address: Address
  decimals: bigint
  symbol: string
  name: string
  image?: string
}

export interface VaultCreator {
  name: string
  description?: string
  logo?: string
}

export interface Collateral {
  address: Address
  asset: Asset
  unitOfAccount: Asset
  liquidationLtv: FormattedValue
  borrowLtv: FormattedValue
  maxLeverage: FormattedValue
}

export type GetRewardVaultInfo = {
  reward: Address
  rewardPrice: number
  rewardLogo: string
  nextRewardAmount?: string
  rewardDecimals: number
  rewardName: string
  amountTotals: string
  totalRewardedEligible?: string
  epochDuration: number
  rewardAmount?: string
  rewardSymbol: string
  currentEpoch: number
  epochEnd: number
  lastEpoch: number
  lastEpochEnd: number
  vault: Address
}

export interface Reward {
  asset: Asset
  price: FormattedAssetValue
  rewardAmount: FormattedAssetValue
  nextRewardAmount: FormattedAssetValue
  active: boolean
  apy: number
  nextApy: number
}

export enum VaultType {
  Custom = "customVaultList",
  Escrow = "escrowVaults",
  Governed = "governedVaults",
  Factory = "factoryVaults",
  EarnFactory = "earnFactoryVaults",
  Unknown = "unknownVaults",
  Edge = "edgeVaults",
}

export interface ExperienceMarket {
  description?: string
  entity: readonly string[]
  logo: string
  name: string
  token?: string
  url?: string
  collateralVaults?: readonly Address[]
  liabilityVaults?: readonly Address[]
}

export enum GovernorType {
  UNKNOWN = "UNKNOWN",
  KNOWN = "KNOWN",
  UNGOVERNED = "UNGOVERNED",
}

export interface GovernorInfo {
  address?: Address
  key?: string
  logo?: string
  name?: string
  type: GovernorType
}

export interface VaultGovernors {
  governors: GovernorInfo[]
  hasGovernorAddressMismatch?: boolean
}

export interface Vault {
  /// meta
  address: Address
  asset: Asset
  customName?: string
  name: string
  symbol: string
  unitOfAccount: Asset
  vaultCreator?: VaultCreator
  vaultType: VaultType
  /// values
  availableToBorrow: FormattedAssetValue
  borrowApy: FormattedValue
  exchangeRate: FormattedValue
  price: FormattedAssetValue
  supplyApy: FormattedValue
  totalAssets: FormattedAssetValue
  totalBorrowed: FormattedAssetValue
  totalCash: FormattedAssetValue
  totalShares: FormattedAssetValue
  utilisation: FormattedValue
  walletBalance?: FormattedAssetValue
  /// parallel values
  collaterals?: Collateral[]
  nativeApy?: number
  rewards?: Reward[]
  points?: ExperienceMarket[]
  /// raw
  raw: NonNullable<GetVaultData>
}
